import type { UploadedImage } from '@/types/image'
import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed')
  }

  return data
}

export interface UploadResponse {
  image: UploadedImage
}

export interface ViewUrlResponse {
  url: string
  thumbnailUrl: string
  expiresAt: string
}

export const imagesApi = {
  /**
   * Upload a single image
   * Uses multipart/form-data instead of JSON
   */
  async upload(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> {
    const accessToken = useAuthStore.getState().accessToken

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const formData = new FormData()
      formData.append('file', file)

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        try {
          const data = JSON.parse(xhr.responseText)
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data)
          } else {
            reject(new ApiError(xhr.status, data.error || 'Upload failed'))
          }
        } catch {
          reject(new ApiError(xhr.status, 'Invalid response'))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new ApiError(0, 'Network error'))
      })

      xhr.addEventListener('abort', () => {
        reject(new ApiError(0, 'Upload aborted'))
      })

      xhr.open('POST', `${API_URL}/api/images/upload`)
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
      xhr.withCredentials = true
      xhr.send(formData)
    })
  },

  /**
   * Get fresh signed URLs for viewing an image
   */
  async getViewUrls(imageId: string): Promise<ViewUrlResponse> {
    return fetchWithAuth<ViewUrlResponse>(`/api/images/${imageId}/view`)
  },

  /**
   * Delete an image
   */
  async delete(imageId: string): Promise<{ success: boolean }> {
    return fetchWithAuth<{ success: boolean }>(`/api/images/${imageId}`, {
      method: 'DELETE',
    })
  },
}

export { ApiError }
